import { EstimationSettingsParams, ClientReportSettingWithDate, SettingsWithDateForUpdate } from "@/store/analytics/estimation_settings";
import { useHistoryStore } from "@/store/history/history";
import { convertDateFromISOWithShortYear } from "../utils/DateParser";

export function setEstimationSettingsParamsByFilter(params: EstimationSettingsParams, settings: TableSettings) {
    if (settings.orderField) {
        params.order_by = settings.orderField;
    } else {
        params.order_by = "-mtime";
    }

    if (settings.commonFilter) {
        params.strategy__ilike = settings.commonFilter;
    }

    if (Object.keys(settings.filter).length > 0) {
        if (settings.filter.strategy) {
            params.strategy__ilike = settings.filter.strategy;
        }

        if (settings.filter.user.length > 0) {
            params.cuser__id__in = settings.filter.user.join(",");
        }

        if (settings.filter.brand.length > 0) {
            params.brand_pk__in = settings.filter.brand.join(",");
        }
    }
}

export function getHistories(resp: any, paramsStoreMap: Map<number, string>, settingsMap: Map<any, any>) {
    const mapTransactions = new Map<number, any>();
    const historyStore = useHistoryStore();

    const processChange = (obj: any, history: any, key: string, settingId: number) => {
        if (key === "brand_pk" || key === "strategy") {
            if (!obj[key]) {
                let newValue = history.changes[key].new;
                let oldValue = history.changes[key].old;

                if (oldValue != null) {
                    if (key === "brand_pk") {
                        newValue = newValue?.brand_name;
                        oldValue = oldValue?.brand_name;
                    }

                    obj.changes.push({
                        field: historyStore.fieldsMap.get(key),
                        new: newValue,
                        old: oldValue,
                    });
                    obj[key] = true;
                }
            }
            return;
        }

        if (key === "value") {
            const param_id = settingsMap.get(settingId); // Используем settingId вместо history.id
            const param_name = paramsStoreMap.get(parseInt(param_id));
            obj.changes.push({
                field: `Значение для параметра ${param_name}`,
                new: history.changes[key].new,
                old: history.changes[key].old,
            });
            return;
        }

        if (key === "start_date" && history.changes[key].new !== "1900-01-01T00:00:00") {
            const new_date = history.changes[key].new
                ? convertDateFromISOWithShortYear(history.changes[key].new)
                : null;
            const old_date = history.changes[key].old
                ? convertDateFromISOWithShortYear(history.changes[key].old)
                : null;

            obj.changes.push({
                field: `Дата начала действия коэффициента`,
                new: new_date,
                old: old_date,
            });
        }
    };

    resp.forEach((e: any) => {
        e.histories.forEach((history: any) => {
            const obj = mapTransactions.get(history.transaction_id) || {
                operation_type: history.operation_type,
                changed_at: history.changed_at,
                changed_by: history.changed_by,
                changes: [],
                brand_pk: false,
                strategy: false,
            };

            if (history.operation_type !== "INSERT") {
                Object.keys(history.changes).forEach(key => {
                    processChange(obj, history, key, e.id); // Передаем e.id как settingId
                });
            } else {
                // Для INSERT обрабатываем только value и start_date
                if ("value" in history.changes) {
                    processChange(obj, history, "value", e.id);
                }
                if ("start_date" in history.changes) {
                    processChange(obj, history, "start_date", e.id);
                }
            }

            mapTransactions.set(history.transaction_id, obj);
        });
    });

    return Array.from(mapTransactions.entries())
        .sort(([keyA], [keyB]) => keyA - keyB)
        .map(([, value]) => value);
}

export function prepareSettings(data: ClientReportSettingWithDate[]) {
    const grouped = data.reduce((acc, item) => {
        const key = item.client_reports_params_id;

        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(item);
        return acc;
    }, {} as Record<number, ClientReportSettingWithDate[]>);

    for (const groupId in grouped) {
        grouped[groupId].sort((a, b) =>
            new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
        );

        grouped[groupId].map(e => e.start_date_formatted = new Date(e.start_date))
    }

    return grouped;
}

export function isSettignsFilledCorrectly(
    settingsRecord: Record<number, ClientReportSettingWithDate[]>
): boolean {
    return Object.values(settingsRecord).every(settingsArray => {
        // Проверка на null
        if (settingsArray.some(s => s.start_date_formatted === null || s.value === null)) {
            return false;
        }

        // Проверка на дубликаты дат
        const dateSet = new Set<string>();
        return settingsArray.every(setting => {
            if (!setting.start_date_formatted) return false;

            const dateKey = setting.start_date_formatted.toISOString().split('T')[0];
            if (dateSet.has(dateKey)) return false;

            dateSet.add(dateKey);
            return true;
        });
    });
}

function formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T00:00:00`;
}


export function transformSettingsToArray(settings: {
    brand_name: string;
    brand_pk: string;
    strategy: string;
    settings: Record<number, ClientReportSettingWithDate[]>;
}): Array<SettingsWithDateForUpdate> {
    return Object.entries(settings.settings).flatMap(([client_reports_params_id, settingsArray]) =>
        settingsArray
            .filter(setting => setting.start_date_formatted)
            .map(setting => ({
                ...(setting.id !== 0 && { id: setting.id }),
                brand_pk: settings.brand_pk,
                strategy: settings.strategy,
                value: setting.value,
                client_reports_params_id: Number(client_reports_params_id),
                start_date: formatDate(setting.start_date_formatted!),
            }))
    );
}

interface TableSettings {
    first: number;
    orderField: string;
    commonFilter: string;
    filter: FilterForm;
}

export interface FilterForm {
    strategy: string;
    user: number[];
    brand: string[];
}